import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Mail, MapPin, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { DISTRIBUIDORES } from "@/data/distribuidores";

const wa = (n) => `https://wa.me/${n.replace(/\D/g, "")}`;
const host = (u) => u.replace(/^https?:\/\//, "").replace(/\/$/, "");

function FlagButton({ c, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`flag-${c.code}`} aria-pressed={active}
      className={`group flex flex-col items-center gap-2 rounded-2xl px-2 py-3 transition-[background-color,transform] duration-300 hover:-translate-y-1 ${active ? "bg-[#0f2544]" : "hover:bg-white"}`}>
      <span className={`block w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden ring-2 transition-[box-shadow] duration-300 ${active ? "ring-[#e6c364] shadow-[0_8px_24px_rgba(230,195,100,0.35)]" : "ring-[#0f2544]/10 group-hover:ring-[#1f44e0]/40"}`}>
        <img src={`/flags/${c.code}.svg`} alt="" className="w-full h-full object-cover" loading="lazy" />
      </span>
      <span className={`text-[0.72rem] sm:text-xs font-semibold text-center leading-tight ${active ? "text-white" : "text-[#0f2544]"}`}>{c.pais}</span>
    </button>
  );
}

function Row({ icon: Icon, children }) {
  return <p className="flex items-start gap-2.5 text-sm text-[#4a4a45]"><Icon size={15} className="text-[#1f44e0] mt-0.5 shrink-0" /><span className="min-w-0 break-words">{children}</span></p>;
}

function DistCard({ x, k }) {
  return (
    <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: k * 0.05, duration: 0.4 }}
      className="rounded-2xl bg-white border border-[#0f2544]/10 p-6 hover:border-[#1f44e0]/30 hover:shadow-[0_12px_32px_rgba(15,37,68,0.08)] transition-[border-color,box-shadow] duration-300" data-testid={`dist-card-${k}`}>
      <p className="text-[0.65rem] font-bold tracking-[0.2em] uppercase text-[#1f44e0]">{x.ciudad}</p>
      <h3 className="font-serif text-xl font-semibold text-[#0f2544] mt-1.5 leading-snug">{x.nombre}</h3>
      <div className="mt-4 space-y-2.5">
        <Row icon={MapPin}>{x.direccion}</Row>
        {x.whatsapp && <Row icon={MessageCircle}><a href={wa(x.whatsapp)} target="_blank" rel="noopener noreferrer" className="hover:text-[#1f44e0]">{x.whatsapp}</a></Row>}
        {x.email && <Row icon={Mail}><a href={`mailto:${x.email}`} className="hover:text-[#1f44e0]">{x.email}</a></Row>}
        {x.web && <Row icon={Globe}><a href={x.web} target="_blank" rel="noopener noreferrer" className="hover:text-[#1f44e0]">{host(x.web)}</a></Row>}
      </div>
    </motion.article>
  );
}

export default function Distribuidores() {
  const [code, setCode] = useState("es");
  const c = DISTRIBUIDORES.find((x) => x.code === code);
  const pick = (k) => {
    setCode(k);
    if (window.innerWidth < 1024) document.getElementById("dist-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <>
      <Seo title="Distribuidores" description="Distribuidores de Editorial Harvest por país: dirección, WhatsApp, email y web." />
      <section className="max-w-[1500px] mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-24" data-testid="distribuidores-page">
        <p className="hv-overline">Dónde encontrarnos</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0f2544] mt-3">Distribuidores</h1>
        <p className="text-base md:text-lg text-[#4a4a45] mt-5 max-w-2xl">Toca la bandera de tu país para ver los distribuidores disponibles y sus datos de contacto.</p>
        <div className="mt-12 grid lg:grid-cols-[minmax(0,420px)_1fr] gap-10 lg:gap-14 items-start">
          <div className="rounded-3xl bg-[#eceae4] p-3 sm:p-4 grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-4 gap-1 lg:sticky lg:top-28" data-testid="flag-grid">
            {DISTRIBUIDORES.map((x) => <FlagButton key={x.code} c={x} active={x.code === code} onClick={() => pick(x.code)} />)}
          </div>
          <div id="dist-panel" className="scroll-mt-24" data-testid="dist-panel">
            <AnimatePresence mode="wait">
              <motion.div key={code} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                <div className="flex items-center gap-4 mb-6">
                  <img src={`/flags/${c.code}.svg`} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#e6c364]" />
                  <div>
                    <h2 className="font-serif text-3xl font-semibold text-[#0f2544]" data-testid="dist-country">{c.pais}</h2>
                    <p className="text-sm text-[#8a8a83]">{c.items.length} {c.items.length === 1 ? "distribuidor" : "distribuidores"}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {c.items.map((x, k) => <DistCard key={`${x.nombre}-${k}`} x={x} k={k} />)}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </>
  );
}
